import React from "react"
import { useNavigate } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

export interface PreviewEntity {
  type: string
  id: string
  path: string
  data?: any
  title?: string
  subtitle?: string
}

interface SearchPreviewModalProps {
  entity: PreviewEntity | null
  onClose: () => void
}

const formatCurrency = (value: any) => {
  const num = Number(value)
  if (isNaN(num)) return "-"
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(num)
}

const formatDate = (value: any) => {
  if (!value) return "-"
  try {
    return new Date(value).toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  } catch {
    return String(value)
  }
}

const statusColor = (status: string) => {
  const s = (status || "").toLowerCase()
  if (s === "paid" || s === "pagada" || s === "completed" || s === "completado" || s === "activo" || s === "active" || s === "delivered" || s === "entregado")
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
  if (s === "pending" || s === "pendiente" || s === "draft" || s === "borrador")
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
  if (s === "cancelled" || s === "cancelada" || s === "anulada" || s === "rejected")
    return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
  return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
}

const Field: React.FC<{ label: string; value: any; badge?: boolean }> = ({ label, value, badge }) => {
  if (!value && value !== 0) return null
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      {badge ? (
        <Badge variant="secondary" className={`w-fit text-xs ${statusColor(String(value))}`}>
          {String(value)}
        </Badge>
      ) : (
        <span className="text-sm font-medium text-foreground">{String(value)}</span>
      )}
    </div>
  )
}

const typeLabels: Record<string, string> = {
  client: "Cliente",
  product: "Producto",
  provider: "Proveedor",
  invoice: "Factura",
  budget: "Presupuesto",
  remito: "Remito",
  purchase: "Orden de Compra",
  supply: "Insumo",
  appointment: "Turno",
  diagnostic: "Diagnóstico",
}

const typeColors: Record<string, string> = {
  client: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  product: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  provider: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400",
  invoice: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  budget: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400",
  remito: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
  purchase: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  supply: "bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-400",
  appointment: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400",
  diagnostic: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400",
}

const ClientPreview: React.FC<{ data: any }> = ({ data }) => {
  const name = data.companyName || `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Cliente"
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="Nombre" value={name} />
      <Field label="Tipo" value={data.type === "company" ? "Empresa" : "Persona"} />
      <Field label="Email" value={data.email} />
      <Field label="Teléfono" value={data.phone} />
      <Field label="WhatsApp" value={data.whatsapp} />
      <Field label="CUIT" value={data.cuit} />
      <Field label="DNI" value={data.dni} />
      <Field label="Categoría" value={data.category} badge />
      <Field label="Dirección" value={data.address} />
      <Field label="Ciudad" value={data.city} />
      <Field label="Provincia" value={data.province} />
      <Field label="Total Gastado" value={data.totalSpent ? formatCurrency(data.totalSpent) : null} />
      <Field label="Servicios" value={data.serviceCount} />
      <Field label="Último Servicio" value={data.lastServiceDate ? formatDate(data.lastServiceDate) : null} />
    </div>
  )
}

const ProductPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Nombre" value={data.name} />
    <Field label="Tipo" value={data.isService ? "Servicio" : "Producto"} />
    <Field label="SKU" value={data.sku} />
    <Field label="Código de Barras" value={data.barcode} />
    <Field label="Categoría" value={data.category} />
    <Field label="Precio de Venta" value={data.salePrice ? formatCurrency(data.salePrice) : null} />
    <Field label="Costo" value={data.costPrice ? formatCurrency(data.costPrice) : null} />
    <Field label="Stock" value={data.stock} />
    <Field label="Unidad" value={data.unit} />
    {data.description && (
      <div className="col-span-2">
        <Field label="Descripción" value={data.description} />
      </div>
    )}
  </div>
)

const ProviderPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Nombre" value={data.name} />
    <Field label="Empresa" value={data.companyName} />
    <Field label="Email" value={data.email} />
    <Field label="Teléfono" value={data.phone} />
    <Field label="CUIT" value={data.taxId} />
    <Field label="Dirección" value={data.address} />
    <Field label="Ciudad" value={data.city} />
    <Field label="Provincia" value={data.province} />
    {data.notes && (
      <div className="col-span-2">
        <Field label="Notas" value={data.notes} />
      </div>
    )}
  </div>
)

const InvoicePreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Número" value={data.invoiceNumber} />
    <Field label="Tipo" value={data.invoiceType} />
    <Field label="Cliente" value={data.clientName} />
    <Field label="Documento" value={data.clientDocument} />
    <Field label="Estado" value={data.status} badge />
    <Field label="Fecha" value={formatDate(data.issueDate)} />
    <Field label="Total" value={formatCurrency(data.total)} />
    <Field label="Método de Pago" value={data.paymentMethod} />
  </div>
)

const BudgetPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Número" value={data.budgetNumber} />
    <Field label="Cliente" value={data.clientName} />
    <Field label="Estado" value={data.status} badge />
    <Field label="Total" value={formatCurrency(data.total)} />
    <Field label="Válido Hasta" value={formatDate(data.validUntil)} />
    <Field label="Creado" value={formatDate(data.createdAt)} />
    {data.description && (
      <div className="col-span-2">
        <Field label="Descripción" value={data.description} />
      </div>
    )}
  </div>
)

const RemitoPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Número" value={data.remitNumber} />
    <Field label="Cliente" value={data.clientName} />
    <Field label="Estado" value={data.status} badge />
    <Field label="Fecha Emisión" value={formatDate(data.issuedAt)} />
    <Field label="Creado" value={formatDate(data.createdAt)} />
    {data.notes && (
      <div className="col-span-2">
        <Field label="Notas" value={data.notes} />
      </div>
    )}
  </div>
)

const PurchasePreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Número" value={data.purchaseNumber} />
    <Field label="Proveedor" value={data.providerName} />
    <Field label="Estado" value={data.status} badge />
    <Field label="Total" value={formatCurrency(data.total)} />
    <Field label="Fecha" value={formatDate(data.date)} />
    {data.notes && (
      <div className="col-span-2">
        <Field label="Notas" value={data.notes} />
      </div>
    )}
  </div>
)

const SupplyPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Nombre" value={data.name} />
    <Field label="Categoría" value={data.category} />
    <Field label="Proveedor" value={data.supplier} />
    <Field label="Stock" value={data.stock} />
    <Field label="Stock Mínimo" value={data.minStock} />
    <Field label="Unidad" value={data.unit} />
    <Field label="Costo" value={data.costPrice ? formatCurrency(data.costPrice) : null} />
  </div>
)

const AppointmentPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Código" value={data.appointmentCode} />
    <Field label="Cliente" value={data.clientName} />
    <Field label="Servicio" value={data.serviceType} />
    <Field label="Estado" value={data.status} badge />
    <Field label="Fecha" value={formatDate(data.scheduledDate)} />
    <Field label="Patente" value={data.licensePlate} />
    <Field label="Vehículo" value={[data.make, data.model].filter(Boolean).join(" ")} />
    {data.notes && (
      <div className="col-span-2">
        <Field label="Notas" value={data.notes} />
      </div>
    )}
  </div>
)

const DiagnosticPreview: React.FC<{ data: any }> = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Número" value={data.diagnosisNumber} />
    <Field label="Cliente" value={data.clientName} />
    <Field label="Estado" value={data.status} badge />
    <Field label="Patente" value={data.licensePlate} />
    <Field label="Vehículo" value={[data.make, data.model].filter(Boolean).join(" ")} />
    <Field label="Creado" value={formatDate(data.createdAt)} />
    {data.notes && (
      <div className="col-span-2">
        <Field label="Notas" value={data.notes} />
      </div>
    )}
  </div>
)

const previewComponents: Record<string, React.FC<{ data: any }>> = {
  client: ClientPreview,
  product: ProductPreview,
  provider: ProviderPreview,
  invoice: InvoicePreview,
  budget: BudgetPreview,
  remito: RemitoPreview,
  purchase: PurchasePreview,
  supply: SupplyPreview,
  appointment: AppointmentPreview,
  diagnostic: DiagnosticPreview,
}

const SearchPreviewModal: React.FC<SearchPreviewModalProps> = ({ entity, onClose }) => {
  const navigate = useNavigate()

  if (!entity) return null

  const PreviewComponent = previewComponents[entity.type]
  const typeLabel = typeLabels[entity.type] || entity.type
  const typeColor = typeColors[entity.type] || "bg-gray-100 text-gray-800"

  const handleGoToPage = () => {
    onClose()
    navigate(entity.path)
  }

  return (
    <Dialog open={!!entity} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="secondary" className={`text-xs font-medium ${typeColor}`}>
                  {typeLabel}
                </Badge>
              </div>
              <DialogTitle className="text-lg font-semibold truncate">
                {entity.title || "Sin título"}
              </DialogTitle>
              {entity.subtitle && (
                <p className="text-sm text-muted-foreground mt-0.5 truncate">{entity.subtitle}</p>
              )}
            </div>
          </div>
        </DialogHeader>

        <Separator className="my-0" />

        <ScrollArea className="px-6 py-4 max-h-[50vh]">
          {PreviewComponent != null && entity.data ? (
            <PreviewComponent data={entity.data} />
          ) : (
            <div className="text-sm text-muted-foreground py-4 text-center">
              No hay datos adicionales disponibles.
            </div>
          )}
        </ScrollArea>

        <Separator className="my-0" />

        <div className="px-6 py-4 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button size="sm" onClick={handleGoToPage} className="gap-1.5">
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            Ir a página completa
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SearchPreviewModal
